import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Gamepad2 } from 'lucide-react';

const HERO_BG =
  'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/th5q7m1d_image.png';

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden vignette"
      data-testid="hero-section"
    >
      {/* Golem hero portrait — positioned right so it doesn't clash with the title */}
      <div
        className="absolute inset-0 bg-no-repeat"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'auto 120%',
          backgroundPosition: 'right -6% center',
          filter: 'saturate(0.95) contrast(1.05) brightness(1.15)',
        }}
      />
      {/* Left-heavy scrim so the title stays readable but character shows through more */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/55 to-black/5" />
      {/* Bottom fade into the site background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0a0a0a]" />
      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>\")",
        }}
      />

      {/* Top label */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3"
        >
          <span className="tag" data-testid="hero-availability">
            <span className="tag-dot" /> Open to Internships &amp; Junior Game Roles
          </span>
        </motion.div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
          className="font-display font-black text-white leading-[0.92] text-[18vw] sm:text-[16vw] lg:text-[12rem] xl:text-[14rem] tracking-tighter"
          data-testid="hero-title"
        >
          GAME
          <br />
          <span className="inline-flex items-baseline gap-4">
            DESIGN
            <span className="text-[#e58a59] font-light italic text-[8vw] lg:text-7xl xl:text-8xl translate-y-[-1vw]">
              /craft
            </span>
          </span>
        </motion.h1>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="lg:col-span-6 text-zinc-300 text-lg leading-relaxed max-w-xl"
            data-testid="hero-tagline"
          >
            A portfolio of three ZBrush characters and a custom-built RTS in
            Unreal Engine — a study in form, systems, and the quiet mechanics
            that make worlds feel inhabited.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="lg:col-span-6 flex flex-wrap items-center gap-3 lg:justify-end"
          >
            <button
              onClick={() => document.getElementById('rts')?.scrollIntoView({ behavior: 'smooth' })}
              className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#e58a59] text-black font-semibold hover:bg-[#f0a075] transition-colors"
              data-testid="hero-cta-rts"
            >
              <Gamepad2 size={18} />
              Explore the RTS
            </button>
            <button
              onClick={() => document.getElementById('zbrush')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-white/20 text-white hover:border-white/60 transition-colors"
              data-testid="hero-cta-zbrush"
            >
              View Sculpts
            </button>
          </motion.div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 backdrop-blur-md bg-black/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-400">
          <span data-testid="hero-strip-meta">Portfolio — Vol. I — 2026</span>
          <button
            onClick={() => document.getElementById('zbrush')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center gap-2 hover:text-white transition-colors"
            data-testid="hero-scroll-cue"
          >
            Scroll
            <ArrowDown size={14} className="animate-bounce" />
          </button>
          <span className="hidden sm:inline">ZBrush · Unreal · Systems Design</span>
        </div>
      </div>
    </section>
  );
}
